import { SIGNAL_NAMES, signalFilled, type ConciergeSignals, type SignalName } from './conciergeState'

export type RecommendationReadiness = {
  ready: boolean
  filledCount: number
  reason: string
}

// We hold the first fetch until we have two details, and at least one that says
// who it's for or what they're into — a location or budget alone can't seed a
// search. Two pieces keep the results from being uselessly broad.
export function recommendationReadiness (signals: ConciergeSignals): RecommendationReadiness {
  const hasTargeting = signalFilled(signals.relationship)
    || signalFilled(signals.occasion)
    || signalFilled(signals.interests)

  const filledCount = SIGNAL_NAMES.filter(name => signalFilled(signals[name])).length
  const ready = hasTargeting && filledCount >= 2

  return {
    ready,
    filledCount,
    reason: ready
      ? 'Enough to start finding gifts.'
      : hasTargeting
        ? 'Add one more detail to start.'
        : "Tell us who it's for or what they're into.",
  }
}

// Which still-missing detail to nudge for next, most useful first. Location leads
// because it pairs with anything. Budget is intentionally omitted — we don't
// encourage a price point (it's still captured passively if the buyer offers one).
const NEXT_FIELDS: SignalName[] = ['location', 'relationship', 'occasion', 'interests']

export function nextSignalPrompt (signals: ConciergeSignals): SignalName | null {
  return NEXT_FIELDS.find(field => !signalFilled(signals[field])) ?? null
}

// Completes the buyer's text with a ghost phrase, dropping any leading words they
// already typed — "my sister in" + "in Seattle" → "Seattle", not "in in Seattle".
export function ghostContinuation (text: string, phrase: string): string {
  const textWords = text.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const phraseWords = phrase.split(' ')
  let overlap = 0
  for (let k = Math.min(phraseWords.length, textWords.length); k > 0; k--) {
    if (textWords.slice(-k).join(' ') === phraseWords.slice(0, k).join(' ').toLowerCase()) {
      overlap = k
      break
    }
  }
  return phraseWords.slice(overlap).join(' ')
}
